// ERDVisualization.jsx

import React, { useState } from 'react';
import { MdPictureAsPdf, MdZoomIn, MdZoomOut, MdRefresh } from 'react-icons/md';

const ZOOM_MIN = 25;
const ZOOM_MAX = 200;
const ZOOM_STEP = 25;

const ERDVisualization = ({ svgContent, erdNotation, username, environment }) => {
  const [zoomLevel, setZoomLevel] = useState(100);

  const zoomIn = () => setZoomLevel(prev => Math.min(prev + ZOOM_STEP, ZOOM_MAX));
  const zoomOut = () => setZoomLevel(prev => Math.max(prev - ZOOM_STEP, ZOOM_MIN));
  const resetZoom = () => setZoomLevel(100);

  // Generate the HTML page that will be printed to PDF
  const generatePDFContent = (svgData) => {
    const today = new Date();
    // Watermark: plain HTML text, white on white background.
    // Chrome's PDF renderer converts HTML text to real PDF text objects (not rasterized),
    // so this survives print-to-PDF and is extractable by pdfminer / pdftotext.
    // SVG text is NOT used because Chrome rasterizes SVG content during print.
    const chicagoDate = today.toLocaleString('en-US', { timeZone: 'America/Chicago' });
    const wmPayload = `AISYS_WM|u:${username || 'unknown'}|e:${environment || 'unknown'}|d:${chicagoDate} CT`;
    return `
      <!DOCTYPE html>
      <html>
      <head>
        <title>ERD Diagram - ${environment}</title>
        <style>
          body {
            margin: 0;
            padding: 20px;
            font-family: Arial, sans-serif;
            background: white;
          }
          .header {
            text-align: center;
            margin-bottom: 20px;
            border-bottom: 2px solid #333;
            padding-bottom: 10px;
          }
          .diagram-container {
            display: flex;
            justify-content: center;
            align-items: center;
            width: 100%;
            min-height: 400px;
          }
          .diagram-container svg {
            max-width: 100%;
            height: auto;
          }
          .footer {
            margin-top: 20px;
            text-align: center;
            font-size: 12px;
            color: #666;
            border-top: 1px solid #ccc;
            padding-top: 10px;
          }
          @media print {
            body { margin: 0; }
            .no-print { display: none; }
          }
        </style>
      </head>
      <body>
        <div class="header">
          <h1>Entity-Relationship Diagram</h1>
          <h2>Environment: ${environment}</h2>
          <p>Student: ${username} | Date: ${today.toLocaleDateString('en-US', { timeZone: 'America/Chicago' })} CT</p>
        </div>
        <div class="diagram-container">
          ${svgData}
        </div>
        <div class="footer">
          <p>Generated on ${today.toLocaleString('en-US', { timeZone: 'America/Chicago' })} CT</p>
          <span style="color:white;font-size:1px;line-height:1px;display:block;">${wmPayload}</span>
        </div>
      </body>
      </html>
    `;
  };

  // Open a print dialog so the user can save as PDF
  const handleDownloadPDF = async () => {
    if (!svgContent) return;

    try {
      let svgData = svgContent;
      if (svgContent.includes('data:image/svg+xml;base64,')) {
        const base64Match = svgContent.match(/data:image\/svg\+xml;base64,([^"]*)/);
        if (base64Match) {
          try {
            svgData = atob(base64Match[1]);
          } catch (e) {
            console.error('Error decoding base64 SVG:', e);
            return;
          }
        }
      }

      const printWindow = window.open('', '_blank');
      printWindow.document.write(generatePDFContent(svgData));
      printWindow.document.close();

      printWindow.onload = () => {
        setTimeout(() => {
          printWindow.print();
          setTimeout(() => printWindow.close(), 1000);
        }, 500);
      };
    } catch (error) {
      console.error('Error generating PDF:', error);
      alert('Error generating PDF. Please try again.');
    }
  };

  return (
    <div className="border border-gray-300 rounded-md bg-gray-50 mt-4">
      <div className="flex justify-between items-center p-4 pb-2">
        <h3 className="font-medium text-gray-800">ERD Diagram:</h3>
        <div className="flex gap-2">
          {/* Zoom Controls */}
          <div className="flex items-center gap-1 border border-gray-200 rounded-md overflow-hidden">
            <button
              onClick={zoomOut}
              disabled={zoomLevel <= ZOOM_MIN}
              className="p-1.5 text-gray-600 hover:bg-gray-100 hover:text-gray-800 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
              title="Zoom out"
            >
              <MdZoomOut size={16} />
            </button>
            <span className="text-xs text-gray-600 w-11 text-center select-none">
              {zoomLevel}%
            </span>
            <button
              onClick={zoomIn}
              disabled={zoomLevel >= ZOOM_MAX}
              className="p-1.5 text-gray-600 hover:bg-gray-100 hover:text-gray-800 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
              title="Zoom in"
            >
              <MdZoomIn size={16} />
            </button>
            <button
              onClick={resetZoom}
              disabled={zoomLevel === 100}
              className="p-1.5 text-gray-600 hover:bg-gray-100 hover:text-gray-800 disabled:opacity-30 disabled:cursor-not-allowed transition-colors border-l border-gray-200"
              title="Reset zoom"
            >
              <MdRefresh size={16} />
            </button>
          </div>

          {/* PDF Download Button */}
          <button
            onClick={handleDownloadPDF}
            className="flex items-center gap-2 px-3 py-1 text-red-600 hover:text-red-800 hover:bg-red-50 text-sm transition-colors rounded-md border border-red-200"
            disabled={!svgContent}
            title="Download as PDF"
          >
            <MdPictureAsPdf size={16} />
            Download PDF
          </button>
        </div>
      </div>

      {/* Gradescope Notice */}
      <div className="px-4 pb-2">
        <div className="bg-blue-50 border border-blue-200 rounded-md p-2">
          <p className="text-xs text-blue-800">
            <strong>For Gradescope:</strong> Click "Download PDF" — your browser will open a print dialog where you can save the diagram as a PDF file.
          </p>
        </div>
      </div>

      <div className="p-4 pt-2 bg-white m-2 rounded border overflow-auto">
        <div
          dangerouslySetInnerHTML={{ __html: svgContent }}
          style={{ width: `${zoomLevel}%` }}
        />
      </div>
    </div>
  );
};

export default ERDVisualization;
